import axios from "axios";

let API_PREFIX = "/api/v1";

export const setApiPrefix = () => {
  // All current hosts that are not localhost should simply
  // cut out ".static" from url to get the API domain. E.g.
  // dreams-staging.static.matav.org.il will point to the staging API url at:
  // dreams-staging.matav.org.il
  if (!window.location.host.includes("localhost")) {
    const url = window.location.origin;
    API_PREFIX = `${url.replace(".static", "")}/api/v1`;
  }

  // If none of the above just use the relative host to our current one
  // leaving the API_PREFIX global variable untouched.
};

const extractResponse = (res) => {
  if (res.data && res.data.error) {
    throw res.data;
  }
  return res.data;
};

export const fetchList = async (
  resoursePath,
  {
    scopePrefix = "",
    current,
    pageSize: limit,
    order = {},
    filter = {},
    query = {},
    fields = "",
  }
) => {
  const orders = order.field
    ? [`${order.order === "ascend" ? "+" : "-"}${order.field}`]
    : [];
  const { freeText, ...rest } = filter;
  const params = {
    free_text: freeText,
    query: {
      offset: current ? (current - 1) * limit : undefined,
      limit,
      sort: orders,
      filter: rest,
    },
    fields: fields !== "" ? fields : null,
    ...query,
  };
  return extractResponse(
    await axios.get(`${API_PREFIX}/${scopePrefix}${resoursePath}/`, {
      params,
    })
  );
};

//tenants
export async function getCurrentTenant() {
  return extractResponse(await axios.get(`${API_PREFIX}/current_tenant`));
}

export async function getTenant(id) {
  return extractResponse(await axios.get(`${API_PREFIX}/tenant/${id}`));
}

//registration
export async function acceptMyInvitation(password) {
  return extractResponse(
    await axios.post(`${API_PREFIX}/register/invite/accept`, { password })
  );
}

// user profile
export async function getCurrentUser() {
  return extractResponse(await axios.get(`${API_PREFIX}/profile/current`));
}

// organizationSlice
export async function getOrganization() {
  return extractResponse(await axios.get(`${API_PREFIX}/meta/company/`));
}

export async function getHospitalizationInstitutions() {
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/meta/system_tables/hospitalization_institutions/`
    )
  );
}

export async function getRelativeTypes() {
  return extractResponse(await axios.get(`${API_PREFIX}/meta/relative_types/`));
}

//patients

export async function listPatients(params) {
  return fetchList("patients", params);
}

export async function getPatient(params) {
  const prefix = params.admin ? `` : `branches/${params.branchId}/`;
  return extractResponse(
    await axios.get(`${API_PREFIX}/${prefix}patients/${params.id}`)
  );
}

export async function getGlobalPatient(scopePrefix, params) {
  return extractResponse(
    await axios.get(`${API_PREFIX}/${scopePrefix}global_patients/search/`, {
      params,
    })
  );
}

export async function listLongHospitalizedPatients(params) {
  return fetchList(
    `employees/${params.employeeId}/house_visits/long_hospitalized`,
    params
  );
}

export async function updatePatient(params) {
  return extractResponse(
    await axios.put(
      `${API_PREFIX}/branches/${params.branchId}/patients/${params.patient.id}`,
      params.patient
    )
  );
}

//patients scheduling

export async function listScheduling(params) {
  return fetchList(`patients/${params.patientId}/scheduling`, params);
}

//patients personal_file

export async function listPatientPersonalFile(params) {
  return fetchList(`patients/${params.patientId}/personal_file`, params);
}

//employees

export async function getEmployee(params) {
  return extractResponse(
    await axios.get(`${API_PREFIX}/employees/${params.id}`)
  );
}

export async function updateEmployee({ scopePrefix, employee }) {
  return extractResponse(
    await axios.put(
      `${API_PREFIX}/${scopePrefix}employees/${employee.id}`,
      employee
    )
  );
}

export async function updateEmployeeSignature({
  scopePrefix,
  employeeId,
  signature,
}) {
  return extractResponse(
    await axios.put(`${API_PREFIX}/profile/current/signature`, { signature })
  );
}

//work planSlice

export async function getWorkPlan(params) {
  const prefix = params.admin ? `` : `branches/${params.branchId}/`;
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/${prefix}patients/${params.patientId}/work_plans/current`
    )
  );
}

//house Visits
export async function getHouseVisitSettings({ scopePrefix, patientId }) {
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/${scopePrefix}patients/${patientId}/house_visits/settings`
    )
  );
}

export async function createHouseVisitSettings({
  scopePrefix,
  patientId,
  settings,
}) {
  return extractResponse(
    await axios.post(
      `${API_PREFIX}/${scopePrefix}patients/${patientId}/house_visits/settings`,
      settings
    )
  );
}

export async function updateHouseVisitSettings({
  scopePrefix,
  patientId,
  settings,
}) {
  return extractResponse(
    await axios.put(
      `${API_PREFIX}/${scopePrefix}patients/${patientId}/house_visits/settings/${settings.id}`,
      settings
    )
  );
}

export async function listHouseVisits(params) {
  return fetchList(`patients/${params.patientId}/house_visits/events`, params);
}

export async function listEmployeeHouseVisits(params) {
  return fetchList(
    `employees/${params.employeeId}/house_visits/events`,
    params
  );
}
export async function listControllersHouseVisits(params) {
  return fetchList(
    `employees/${params.employeeId}/house_visits/needs_approval`,
    params
  );
}

export async function approveControllerVisits(params) {
  const { ids, scopePrefix, employeeId } = params;
  return extractResponse(
    await axios.post(
      `${API_PREFIX}/${scopePrefix}employees/${employeeId}/house_visits/verify`,
      { house_visit_ids: ids }
    )
  );
}

export async function getVisitsSummary(params) {
  const { query, scopePrefix, employeeId } = params;
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/${scopePrefix}employees/${employeeId}/house_visits/summary`,
      { params: { ...query } }
    )
  );
}

export async function employeeHouseVisitsUpdate(params) {
  return extractResponse(
    await axios.put(
      `${API_PREFIX}/employees/${params.employeeId}/house_visits/events`,
      params.events
    )
  );
}

export async function employeeHouseVisitsTransfer(params) {
  return extractResponse(
    await axios.post(
      `${API_PREFIX}/employees/${params.employeeId}/house_visits/events/assign-employee`,
      params
    )
  );
}

export async function getHouseVisit(params) {
  const prefix = params.admin ? `` : `branches/${params.branchId}/`;
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/${prefix}patients/${params.patientId}/house_visits/events/${params.houseVisitsId}`
    )
  );
}

export async function getOrders(params) {
  return extractResponse(
    await axios.get(
      `${API_PREFIX}/patients/${params.patientId}/orders/?query=%7B%22offset%22:0,%22limit%22:100,%22sort%22:[%22-effective_from%22],%22filter%22:%7B%7D%7D`
    )
  );
}

export async function createHouseVisit(params) {
  return extractResponse(
    await axios.post(
      `${API_PREFIX}/branches/${params.branchId}/patients/${params.patientId}/house_visits/events/`,
      params.house_visit
    )
  );
}

export async function updateHouseVisit(params) {
  return extractResponse(
    await axios.put(
      `${API_PREFIX}/branches/${params.branchId}/patients/${params.patientId}/house_visits/events/${params.house_visit.id}`,
      params.house_visit
    )
  );
}

export async function deleteHouseVisit(params) {
  return extractResponse(
    await axios.delete(
      `${API_PREFIX}/branches/${params.branchId}/patients/${params.patientId}/house_visits/events/${params.houseVisitId}`
    )
  );
}

export async function getSystemTableData({ name }) {
  return extractResponse(await axios.get(`${API_PREFIX}/${name}/`));
}

export async function listEmployees(params) {
  return fetchList("employees", params);
}

//branches
export async function listBranches(params) {
  return fetchList("branches", params);
}

//settings data

export async function getSettingsData({ name }) {
  return extractResponse(await axios.get(`${API_PREFIX}/meta/${name}/`));
}

export async function listSettingsData(params) {
  return fetchList(`meta/${params.name}`, params);
}

//assets

export async function addEntityAsset(entityName, branchId, id, url, params) {
  const uploaded = await axios.post(
    `${API_PREFIX}/branches/${branchId}/${entityName}/${id}/assets/`,
    {
      url: url,
      ...params,
    }
  );

  return extractResponse(uploaded);
}

export async function generateUploadURL(
  entityName,
  branchId,
  id,
  assetName,
  file
) {
  return await axios.post(
    `${API_PREFIX}/branches/${branchId}/${entityName}/${id}/assets/upload_url`,
    {
      name: assetName,
      content_type: file.type,
    }
  );
}

export async function upload(url, file) {
  const buffer = await readFile(file);
  const uploaded = await axios.put(url, new Uint8Array(buffer), {
    headers: {
      "Content-Type": file.type,
      "Content-Disposition": `attachment; filename=${unescape(
        encodeURIComponent(file.fileName)
      )}`,
    },
  });

  return extractResponse(uploaded);
}

function readFile(file) {
  let reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.readAsArrayBuffer(file);

    reader.onload = function () {
      resolve(reader.result);
    };

    reader.onerror = function () {
      reject(reader.error);
    };
  });
}
